import type { PDFDocumentProxy } from "pdfjs-dist"
import * as React from "react"

import { fetchLabelPdf, fetchMasterPdf } from "../api"

/*
   A PDF of an evaluation run's label (the label itself, or the master it was compared against),
   loaded once per label. Bytes come through `http.bytes`
   (the authenticated transport) and pdf.js gets a copy, since it transfers the buffer to its
   worker. The document is destroyed when the label changes, so stepping through a run's labels
   does not accumulate parsed PDFs.
*/

let workerConfigured = false

async function loadPdfjs(): Promise<typeof import("pdfjs-dist")> {
  const pdfjs = await import("pdfjs-dist")
  if (!workerConfigured) {
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      "pdfjs-dist/build/pdf.worker.min.mjs",
      import.meta.url,
    ).toString()
    workerConfigured = true
  }
  return pdfjs
}

export type PdfState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; doc: PDFDocumentProxy; pointWidth: number; aspect: number }

/** Which of a label's two PDFs: the label the expert marked, or its master. */
export type PdfDocumentKind = "label" | "master"

const FETCH: Record<PdfDocumentKind, typeof fetchLabelPdf> = {
  label: fetchLabelPdf,
  master: fetchMasterPdf,
}

export function usePdfDocument(
  runId: number,
  mid: string,
  enabled: boolean,
  kind: PdfDocumentKind = "label",
): PdfState {
  const [state, setState] = React.useState<PdfState>({ status: "loading" })

  React.useEffect(() => {
    // Disabled (a label with no PDF) is "nothing loaded", never the previous label's document,
    // which this effect's cleanup has already destroyed.
    setState({ status: "loading" })
    if (!enabled) return
    // An object, not a let: the flag flips in the cleanup, which narrowing cannot see.
    const run = { cancelled: false }
    const cancelled = () => run.cancelled
    const abort = new AbortController()
    let destroy: (() => Promise<void>) | null = null
    void (async () => {
      try {
        const [pdfjs, bytes] = await Promise.all([
          loadPdfjs(),
          FETCH[kind](runId, mid, abort.signal),
        ])
        // Left while the bytes were in flight: parse nothing, or the task is never destroyed.
        if (cancelled()) return
        const task = pdfjs.getDocument({ data: new Uint8Array(bytes) })
        destroy = () => task.destroy()
        const doc = await task.promise
        const first = (await doc.getPage(1)).getViewport({ scale: 1 })
        if (!cancelled()) {
          setState({
            status: "ready",
            doc,
            pointWidth: first.width,
            aspect: first.height / first.width,
          })
        }
      } catch (err) {
        if (!cancelled()) {
          setState({
            status: "error",
            message: err instanceof Error ? err.message : "The PDF could not be loaded.",
          })
        }
      }
    })()
    return () => {
      run.cancelled = true
      abort.abort()
      if (destroy) void destroy()
    }
  }, [runId, mid, enabled, kind])

  return state
}
